import React, { useContext, useMemo, useState, useEffect } from 'react'
import { Box, Table, Thead, Th, Tbody, Tr, Td, Text, Spinner, Button, Flex } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { NavLink } from 'react-router'
import { HelmetComponent } from '../../components/HelmetComponent'
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext'
import { useBreadcrumbs } from '../../contexts/BreadcrumbProvider'
import { EDSHOPIFY_DRAFTS, EDSHOPIFY_DRAFTS_COUNT } from '../../graphql/queries'
import { Pagination } from '../../components/Pagination'

export const DraftsPage = () => {
  const { user, publisher } = useContext(PublisherAndUserContext)
  const { addBreadcrumb } = useBreadcrumbs()

  const PageSize = 10
  const [currentPage, setCurrentPage] = useState(1)
  const offset = (currentPage - 1) * PageSize

  const { data, loading, error, refetch, networkStatus } = useQuery(EDSHOPIFY_DRAFTS, {
    variables: { publisherId: publisher?.id, limit: PageSize, offset },
    skip: !publisher?.id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { data: countData } = useQuery(EDSHOPIFY_DRAFTS_COUNT, {
    variables: { publisherId: publisher?.id },
    skip: !publisher?.id,
    fetchPolicy: 'cache-first',
  })

  const drafts = useMemo(() => data?.edshopifyDrafts || [], [data])
  const totalCount = countData?.edshopifyDraftsCount || 0

  useEffect(() => {
    // refetch when page changes
    if (publisher?.id) {
      refetch({ publisherId: publisher.id, limit: PageSize, offset })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, publisher?.id])

  return (
    <>
      <HelmetComponent title={`My Drafts - Home - ${user?.firstName} ${user?.lastName}`} />
      <Box className="content" m="0 auto" minHeight="calc(100vh - 80px)" position="relative">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="5">
          <Text fontSize="lg" fontWeight="700">My Drafts</Text>
        </Box>
        {(loading || networkStatus === 4) && (
          <Box py={10} textAlign="center"><Spinner /></Box>
        )}
        {error && (
          <Box py={6}><Text color="red.500">Failed to load drafts.</Text></Box>
        )}
        {!loading && drafts.length === 0 && (
          <Box py={6}><Text color="gray.600">No drafts found. Start creating a new Book or Course.</Text></Box>
        )}
        {drafts.length > 0 && (
          <Table size="sm" variant="simple" mt="5" mb="5">
            <Thead>
              <Tr>
                <Th fontWeight="800" color="#424770" letterSpacing="wide">Name</Th>
                <Th fontWeight="800" color="#424770" letterSpacing="wide">Type</Th>
                <Th fontWeight="800" color="#424770" letterSpacing="wide">Publish Status</Th>
                <Th fontWeight="800" color="#424770" letterSpacing="wide">Store Status</Th>
                <Th fontWeight="800" color="#424770" letterSpacing="wide">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {drafts.map((p) => {
                const viewPath = `/content/${p.id}`
                return (
                  <Tr key={p.id}>
                    <Td textDecoration="underline">
                      <NavLink to={viewPath} className="catalog-link" onClick={() => addBreadcrumb(p.productName, viewPath, false)}>
                        {p.productName}
                      </NavLink>
                    </Td>
                    <Td fontWeight="600" color={p.productType === 'Book' ? 'purple.500' : 'pink.500'}>{p.productType}</Td>
                    <Td fontWeight="600" color={p.productStatus === 'Published' ? 'green.500' : 'red.500'}>{p.productStatus || 'Offline'}</Td>
                    <Td fontWeight="600" color={p.isAddedToStore ? 'green.500' : 'red.500'}>{p.isAddedToStore ? 'In Store' : 'Not in store'}</Td>
                    <Td>
                      <NavLink to={viewPath} onClick={() => addBreadcrumb(p.productName, viewPath, false)}>
                        <Button size="sm" colorScheme="blue" variant="solid">Edit</Button>
                      </NavLink>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        )}

        {totalCount > PageSize && (
          <Flex justifyContent="center" mt={6}>
            <Pagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={PageSize}
              onPageChange={setCurrentPage}
            />
          </Flex>
        )}
      </Box>
    </>
  )
}
